import React, { Component } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import { spring, StaggeredMotion } from 'react-motion';
import { withRouter, Link } from 'react-router-dom';

import BadgeDisplay from 'components/BadgeDisplay/BadgeDisplay';

import badges from 'assets/images/badges';

import clsx from 'clsx';
import {
  APIHandler,
  DataHandler,
  StorageHandler,
  TrainingCard,
  SubAppButton,
  ContentHeader,
  FastTrack,
  PointsHandler,
} from 'C4';
import {
  storageKeys,
  baseAppId,
  hasFastTrack,
  fastTrackId,
  points,
} from 'settings';

const fastTrackBrackets = [
  { grade: 'A+', range: 97, n: true },
  { grade: 'A', range: 93, n: true },
  { grade: 'A-', range: 90, n: true },
  { grade: 'B+', range: 87 },
  { grade: 'B', range: 83 },
  { grade: 'B-', range: 80 },
  { grade: 'C+', range: 77 },
  { grade: 'C', range: 73 },
  { grade: 'C-', range: 70 },
  { grade: 'D+', range: 67 },
  { grade: 'D', range: 63 },
  { grade: 'D-', range: 60 },
  { grade: 'F', range: 0, n: true },
];

const fastTrackData = (key, value = null) => {
  const data = JSON.parse(localStorage.getItem('fastTrackData')) || {};
  if (key && value === null) {
    if (data) return data[key];
    return null;
  }
  if (key && value) {
    data[key] = value;
    return localStorage.setItem('fastTrackData', JSON.stringify(data));
  }
  throw Error('Invalid input');
};

const setFastTrackGrade = async (grade) => {
  const appId = await DataHandler.matchAppId();
  const { response: user } =
    (await StorageHandler.load(storageKeys.user)) || {};
  if (user) {
    const { email } = user;
    const startTime = fastTrackData('startTime');
    const total = Math.floor((Date.now() - (startTime || 0)) / 1000);
    await APIHandler.updateUser(appId, email, [
      {
        param: 'fast_track_score',
        val:
          (fastTrackData('correctAnswers').length() /
            fastTrackData('totalQuestions')) *
          100,
      },
      { param: 'fast_track_duration', val: total },
    ]);
  }
};

class TrainingGrid extends Component {
  constructor(props) {
    super(props);

    const fastTrackFinished = fastTrackData('finished') || false;
    const fastTrackErrors = +fastTrackData('errors') || 0;

    this.state = {
      appId: 0,
      chapters: [],
      chapters_progress: [],
      subApps: [],
      fastTrackCanceled: fastTrackData('canceled') || false,
      fastTrackErrors,
      fastTrackFinished,
      fastTrackGrade: { grade: '', n: '' },
      totalItemCount: 0,
      totalItemCountCompleted: 0,
      completedSubApps: [],
    };

    this.isLocked = this.isLocked.bind(this);
    this.loadChapters = this.loadChapters.bind(this);
    this.calculateProgress = this.calculateProgress.bind(this);
  }

  componentDidMount() {
    const { fastTrackErrors, fastTrackFinished } = this.state;

    this.mounted = true;

    this.getProgress();
    this.setSubApps(baseAppId);

    if (fastTrackFinished && fastTrackErrors !== -1) {
      this.getGrade(fastTrackErrors);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getProgress() {
    StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        const { email } = res;
        const { appId } = window;
        if (this.mounted) {
          this.setState({ appId });
          APIHandler.getUserProgress(appId, email).then((userProgress) => {
            this.calculateProgress(userProgress);
          });
        }
      }
    });
  }

  async getGrade() {
    const appId = await DataHandler.matchAppId();
    const pages = await APIHandler.getAppQuestions(fastTrackId);
    const correctAnswers = [...new Set(fastTrackData('correctAnswers'))];
    const percentage = (correctAnswers.length / pages.length) * 100;
    const percentage2 = String(Math.round(percentage));

    const [letterGrade] = fastTrackBrackets
      .map(({ grade, range, n }) => {
        if (percentage >= range) {
          return { grade: percentage2, range: n === true ? 'n' : '' };
        }
        return false;
      })
      .filter((val) => val !== false);
    this.setState({ fastTrackGrade: letterGrade });

    const { response: user } = await StorageHandler.load(storageKeys.user);
    if (user) {
      const { email } = user;
      const startTime = fastTrackData('startTime');
      const total = Math.floor((Date.now() - (startTime || 0)) / 1000);
      await APIHandler.updateUser(appId, email, [
        { param: 'fast_track_duration', val: total },
        { param: 'fast_track_score', val: Math.floor(percentage) },
      ]);
    }
  }

  getTieredValue(array = null) {
    const { tiers, totalPoints } = this.pointsHandler;

    let index = 0;
    tiers.forEach(({ points: tierPoints }, i) => {
      // Could be improved, maybe using filter / reduce?
      if (totalPoints >= tierPoints) {
        index = i;
      }
    });

    if (array) return array[index < 0 ? 0 : index];
    return index;
  }

  getSubAppProgress(appIds) {
    return StorageHandler.load(storageKeys.user).then((res) => {
      if (res) {
        const { email } = res;
        if (this.mounted && appIds.apps.length > 0) {
          return APIHandler.getUserProgress(appIds.apps, email).then(
            (userProgress) => {
              const progress = Object.values(userProgress.data).map((app) => {
                const [chapter] = app.chapters;
                const { name } = app;
                return {
                  chapter,
                  name,
                };
              });
              return progress;
            },
          );
        }
      }
      return null;
    });
  }

  setSubApps(appId) {
    return APIHandler.getSubApps(appId).then((ids) => {
      this.getSubAppProgress(ids).then((progress) => {
        if (progress) {
          const subAppPromises = ids.apps.map((id, i) => {
            const pageId =
              progress[i].chapter.last_page < progress[i].chapter.total_pages
                ? progress[i].chapter.last_page
                : 0;
            const key = i;
            return APIHandler.getChapters(id).then((chapter) => {
              const [newApp] = chapter.app;
              return (
                <SubAppButton
                  key={`${key}_${newApp.chapterImage}`}
                  appId={id}
                  progress={progress[i].chapter}
                  name={progress[i].name}
                  pageId={pageId}
                  size={{ height: 50, width: 50 }}
                  type="training"
                />
              );
            });
          });
          if (this.mounted) {
            return Promise.all(subAppPromises).then((subApps) => {
              this.setState({ subApps });
            });
          }
          return null;
        }
        return null;
      });
    });
  }

  async handlePoints() {
    const {
      chapters_progress: progress,
      chapters,
      subApps,
      completedSubApps,
      swal,
      userCountry,
      countryName,
      appId,
    } = this.state;
    const { introChapters } = points;

    const count = 8;
    const intro = progress
      .filter((chapter, i) =>
        introChapters // Filter progress to just the introChapters
          .some(
            (val) =>
              +new RegExp('(.*?-)*(?<index>[0-9]+)', 'g').exec(val).groups
                .index ===
              i + 1,
          ),
      )
      .filter(({ percentage }) => percentage && percentage >= 100);

    const rest = progress
      .filter((chapter, i) =>
        introChapters // Filter introChapters
          .every(
            (val) =>
              +new RegExp('(.*?-)*(?<index>[0-9]+)', 'g').exec(val).groups
                .index !==
              i + 1,
          ),
      )
      .filter(({ percentage }) => percentage && percentage >= 100);

    if (chapters && this.pointsHandler.tiers) {
      const introsComplete = intro.map(({ chapter }) =>
        this.updatePoints('intro-chapter', chapter),
      );
      const chaptersComplete = rest.map(({ chapter }) =>
        this.updatePoints('chapter', chapter),
      );
      const subAppsComplete = completedSubApps.map(({ chapter }) =>
        this.updatePoints('subapp', chapter),
      );

      // Calculate percentage of intro complete completed chapters
      const totalCh = progress.slice(0);
      const totalChCopy = progress.slice(0);
      const introCh = totalCh.splice(0, count);
      const subCh = totalChCopy.splice(count, totalChCopy.length);

      const introProgress = introCh.filter(
        ({ percentage }) => percentage && percentage >= 100,
      );
      const restProgress = subCh.filter(
        ({ percentage }) => percentage && percentage >= 100,
      );

      const introPercentage = Math.floor(
        (introProgress.length / introCh.length) * 100,
      );
      const chaptersPercentage = Math.floor(
        (restProgress.length / subCh.length) * 100,
      );
      const subAppsPercentage = Math.floor(
        (completedSubApps.length / 10) * 100,
      );

      this.setState({
        progressMain: introPercentage,
        progressSub: chaptersPercentage,
        progressSubApps: subAppsPercentage,
      });

      const allUpdates = await Promise.all(
        [introsComplete, chaptersComplete, subAppsComplete].flat(),
      );
      const totalPointsGained = [
        ...allUpdates.map(({ points: p }) => p),
        0,
      ].reduce((total, pointsGained) => total + pointsGained);

      if (!Number.isNaN(totalPointsGained) && totalPointsGained > 0) {
        const [{ points: firstPoints }] = allUpdates.filter(
          ({ points: p }) => p > 0,
        );
        const { tiers, totalPoints } = this.pointsHandler;
        const badgeURl = [];
        let popUp = '';
        const tierIndex =
          tiers && Math.min(this.getTieredValue(), tiers.length - 1);
        const { tierBadges } = badges;

        if (totalPoints === tiers[tierIndex].points) {
          badgeURl.push(Object.values(tierBadges)[tierIndex]);
        } else {
          badgeURl.push(Object.values(tierBadges)[tierIndex]);
          return;
        }

        const user = await StorageHandler.load(storageKeys.user);
        if (tierIndex === 0) {
          popUp = 'bronze';
        } else if (tierIndex === 1) {
          popUp = 'silver';
          if (user) {
            await APIHandler.sendEmail(
              popUp,
              appId,
              user.email,
              user.first_name,
              user.last_name,
            );
          }
        } else if (tierIndex === 2) {
          popUp = 'gold';
          if (user) {
            await APIHandler.sendEmail(
              popUp,
              appId,
              user.email,
              user.first_name,
              user.last_name,
            );
          }
        } else if (tierIndex === 3) {
          popUp = 'platinum';
          if (user) {
            await APIHandler.sendEmail(
              popUp,
              appId,
              user.email,
              user.first_name,
              user.last_name,
            );
          }
        }

        const newSwal = [...swal];
        newSwal.push({
          imageUrl: badgeURl,
          imageWidth: 110,
          imageAlt: `+${firstPoints}XP`,
          showCloseButton: true,
          showConfirmButton: false,
          confirmButtonText:
            popUp === 'silver' ? i18n.translate('nav.reportCard') : 'close',
          icon: '',
          customClass: {
            closeButton: 'trainingGrid__swal2-close',
          },
          html: renderToString(
            <React.Fragment>
              <p>{i18nParsedTranslate(`popUp.${popUp}`, countryName)}</p>
              <PointsAlert
                key={JSON.stringify(points)}
                tiers={tiers}
                points={totalPoints}
                pointsGained={totalPointsGained}
                delay={750}
              />
            </React.Fragment>,
          ),
          onClose: () => {
            if (popUp === 'silver') {
              document.getElementById('showDiplomaButton').click();
            }
            this.setState(
              ({ swal: swalState }) => ({
                swalOpen: false,
                swal: swalState.slice(1),
              }),
              () => {
                const { swal: swalState } = this.state;
                if (swalState.slice(1).length > 0) {
                  this.setState({ swalOpen: true });
                }
              },
            );
          },
          ...(popUp === 'platinum' ? { background: primaryColor } : {}),
        });

        if (totalPoints === tiers[tierIndex].points) {
          this.setState({ swal: newSwal, swalOpen: true });
        }
      }

      const allItemsCompleted = [
        introsComplete.length,
        chaptersComplete.length,
        subAppsComplete.length,
      ];
      const allItems = [progress.length, subApps.length];

      const totalItemCountCompleted = allItemsCompleted.reduce(
        (total, len) => total + len,
      );
      const totalItemCount = allItems.reduce((total, len) => total + len);

      this.setState({ totalItemCountCompleted, totalItemCount });
    }
  }

  async updatePoints(action, i) {
    // TODO: Removed the "< max-points" check to prevent future additional chapters form breaking the updates without a backend api update.
    // const { totalPoints, tiers } = this.pointsHandler;
    // const { points: maxedPoints } = tiers[tiers.length - 1];
    // const userIsMaxed = totalPoints >= maxedPoints;

    // if (!userIsMaxed) {
    const { response } = await this.pointsHandler.setPoints({
      action,
      value: `${action}-${i}`,
    });
    if (response !== 'Already used') {
      const { update } = response;
      this.setState({ refresh: true });
      return update;
    }
    // }
    return { points: 0 };
  }

  async initPointsHandler() {
    this.pointsHandler = new PointsHandler();
    await this.pointsHandler.init();
    await this.pointsHandler.getPoints();
  }

  loadChapters(setProgress) {
    const { appId } = this.state;
    DataHandler.checkKey(
      storageKeys.chapters,
      APIHandler.getChapters(appId),
      true,
    ).then((res) => {
      const startingValues = [];
      Object.keys(res.app).map(() => {
        const newObj = {};
        newObj.data = 0;
        newObj.pos = 20;
        return startingValues.push(newObj);
      });
      if (!setProgress) {
        StorageHandler.load(storageKeys.user).then((user) => {
          APIHandler.registerUserProgress(user.email).then((registered) => {
            if (registered) {
              this.getProgress();
            }
          });
        });
      } else {
        const chapters = (
          <StaggeredMotion
            defaultStyles={startingValues}
            styles={(prevInterpolatedStyles) =>
              prevInterpolatedStyles.map((_, i) =>
                i === 0
                  ? {
                    data: spring(1, { stiffness: 320, damping: 28 }),
                    pos: spring(0, { stiffness: 320, damping: 28 }),
                  }
                  : {
                    data: spring(prevInterpolatedStyles[i - 1].data, {
                      stiffness: 320,
                      damping: 28,
                    }),
                    pos: spring(prevInterpolatedStyles[i - 1].pos, {
                      stiffness: 320,
                      damping: 28,
                    }),
                  },
              )
            }
          >
            {(styles) => (
              <React.Fragment>
                {res.app.map((chapter, key) => (
                  <React.Fragment>
                    {key === 0 && (
                      <span className="subHeader">
                        <ContentHeader
                          header={i18n.translate(
                            'contentHeaders.maintrainings',
                          )}
                          subtitle={i18n.translate(
                            'pageDescriptions.maintrainings',
                          )}
                          line
                        />
                      </span>
                    )}
                    {key === 8 && (
                      <span className="subHeader">
                        <ContentHeader
                          header={i18n.translate('contentHeaders.subtrainings')}
                          subtitle={i18n.translate(
                            'pageDescriptions.subtrainings',
                          )}
                          line
                        />
                      </span>
                    )}
                    <div
                      key={key}
                      className={clsx('trainingCard__Animated', {
                        specialization: key >= 8,
                      })}
                      style={{
                        opacity: `${styles[key].data}`,
                        transform: `translateY(${styles[key].pos}%)`,
                      }}
                    >
                      <TrainingCard
                        chaptersProgress={this.state.chapters_progress[key]}
                        chapter={chapter}
                        chapterCount={chapter.chapterItemCount}
                        chapterCountMin={chapter.chapterItemCountMin}
                        locked={
                          this.isLocked(key) || chapter.coming_soon_visible
                        }
                        index={key}
                        appId={appId}
                        key={chapter.chapterID}
                        comingSoon={chapter.coming_soon_visible}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </React.Fragment>
            )}
          </StaggeredMotion>
        );

        this.setState({ chapters }, async () => {
          await this.initPointsHandler();
          await this.setSubApps(appId);
          await this.handlePoints();
        });
      }
    });
  }

  calculateProgress(res) {
    if (res.status !== 'error') {
      const { chapters } = res.data[0];
      chapters.forEach((chapter) => {
        const newChapter = chapter;
        if (chapter.progress > 0 && chapter.total_pages > 0) {
          newChapter.percentage = Math.floor(
            (chapter.progress / chapter.total_pages) * 100,
          );
          if (newChapter.percentage > 100) {
            newChapter.percentage = 100;
          }
          // Bug fix for last_page higher than progress
          if (
            Math.floor((chapter.total_pages / chapter.last_page) * 100) === 100
          ) {
            newChapter.percentage = 100;
          }
        }
      });
      this.setState({ chapters_progress: chapters }, () => {
        this.loadChapters(res.status !== 'error');
      });
    }
    this.loadChapters(res.status !== 'error');
  }

  isLocked(key) {
    const { chapters_progress: progress } = this.state;
    // If specialization chapter, lock if not all foundation chapters are completed, otherwise unlock
    if (!!progress[key].specialization === true) {
      const foundationChapters = progress.filter(
        (chapter) => !!chapter.specialization === false,
      );
      const foundationChaptersCompleted = foundationChapters.filter(
        (chapter) => chapter.percentage >= 100,
      );
      if (foundationChaptersCompleted.length === foundationChapters.length) {
        return false;
      }
      return true;
    }
    // Lock chapter if it's not the first chapter and the previous chapter isn't completed
    if (
      progress.length > 0 &&
      key !== 0 &&
      progress[key - 1].percentage !== 100
    ) {
      return true;
    }
    // Unlock chapter if it's the first chapter or the previous chapter is completed
    if (
      progress.length > 0 &&
      (key === 0 || progress[key - 1].percentage >= 100)
    ) {
      return false;
    }
    return true;
  }

  render() {
    const { history } = this.props;
    const {
      chapters,
      subApps,
      fastTrackFinished,
      fastTrackErrors,
      fastTrackCanceled,
      fastTrackGrade,
      totalItemCountCompleted,
      totalItemCount,
    } = this.state;

    const { tierBadges } = badges;
    const { initialized, tiers, totalPoints } = this.pointsHandler || {};
    const tierIndex =
      tiers && Math.min(this.getTieredValue(), tiers.length - 1);

    return (
      <React.Fragment>
        {initialized &&
          tiers && ( // Wrap in own component?
            <BadgeDisplay
              tierBadges={tierBadges}
              tierIndex={tierIndex}
              tiers={tiers}
              totalPoints={totalPoints}
              pointsBarSubtitle={i18n.translate('points.completed', {
                completed: totalItemCountCompleted,
                total: totalItemCount,
              })}
              layout="pillar"
              position="right"
            />
          )}
        <div>
          {chapters && subApps && (
            <div>
              <div className="trainingGrid">{chapters}</div>
              {subApps.length > 0 && (
                <React.Fragment>
                  <ContentHeader
                    header={i18n.translate('contentHeaders.subapps')}
                    subtitle={i18n.translate('pageDescriptions.subapps')}
                  />
                  <div className="trainingGrid subApps">{subApps}</div>
                </React.Fragment>
              )}
            </div>
          )}
          <FastTrack
            fastTrackCanceled={fastTrackCanceled}
            fastTrackErrors={+fastTrackErrors}
            fastTrackFinished={fastTrackFinished}
            fastTrackGrade={fastTrackGrade}
            hasFastTrack={hasFastTrack}
            onConfirmAsk={() => {
              this.setState({ fastTrackErrors: '0' }, () => {
                history.push('/main/fasttrack/chapter/0/0');
              });
            }}
            onCancelAsk={() => {
              fastTrackData('canceled', true);
              this.setState({ fastTrackCanceled: true });
            }}
            onConfirmGrade={() => {
              const { grade } = fastTrackGrade || {};
              if (grade.length > 0) {
                setFastTrackGrade(0);
              }
              fastTrackData('errors', -1);
              fastTrackData('grade', grade);
              this.setState({ fastTrackFinished: true, fastTrackErrors: -1 });
            }}
          />
        </div>
      </React.Fragment>
    );
  }
}

TrainingGrid.propTypes = {
  history: PropTypes.shape().isRequired,
};

export default withRouter(TrainingGrid);
